import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, Send, MessageCircle } from "lucide-react";
import { PageHero, DraftNotice } from "@/components/common/SectionHeading";
import SEO, { makeBreadcrumbSchema } from "@/components/common/SEO";
import { fetchProgram } from "@/lib/lnlApi";

const SECTIONS = [
    "Program overview", "Who this program is for", "Skills students may develop", "Possible career paths",
    "Program duration", "Curriculum download", "Admission requirements", "Facilities", "Faculty area",
    "Internship / practicum details", "Licensure information (when applicable)", "Student stories",
    "Program achievements", "Frequently asked questions", "Related programs",
];

export default function Program() {
    const { slug } = useParams();
    const [data, setData] = useState(null);
    useEffect(() => { fetchProgram(slug).then(setData).catch(() => setData(null)); }, [slug]);
    if (!data) return <div className="lnl-container py-16 text-lnl-gray">Loading program…</div>;
    const { program, department } = data;
    const courseSchema = {
        "@context": "https://schema.org",
        "@type": "Course",
        name: program.name,
        description: program.overview,
        provider: {
            "@type": "EducationalOrganization",
            name: "Lyceum Northern Luzon",
            sameAs: "https://lyceumnorthernluzon.edu.ph/",
        },
    };
    return (
        <div data-testid={`program-${slug}`}>
            <SEO
                title={program.name}
                description={program.overview}
                path={`/programs/${program.slug}`}
                type="article"
                schema={courseSchema}
            />
            <PageHero
                eyebrow={department?.name || "Program"}
                title={program.name}
                description={program.overview}
                breadcrumbs={[{ label: "Home", to: "/" }, { label: "Academics", to: "/academics" }, { label: department?.name || "Program", to: `/academics/${department?.slug}` }, { label: program.name }]}
                cta={
                    <div className="mt-6 flex flex-wrap gap-3">
                        <Link to={`/admissions/apply?type=college-freshman&program=${program.slug}`} className="lnl-btn-primary"><Send className="h-4 w-4" /> Apply to this program</Link>
                        <Link to={`/contact?topic=${encodeURIComponent(program.name)}`} className="lnl-btn-secondary"><MessageCircle className="h-4 w-4" /> Request information</Link>
                    </div>
                }
            />
            <section className="py-12 bg-white">
                <div className="lnl-container grid grid-cols-1 lg:grid-cols-3 gap-8">
                    <div className="lg:col-span-2">
                        {program.confirm && <div className="mb-4"><DraftNotice tone="warn">{program.confirm}</DraftNotice></div>}
                        <DraftNotice tone="info">
                            Detailed sections below are managed by administrators. Where an official detail is missing, this page shows a discreet notice instead of placeholder text.
                        </DraftNotice>
                        <div className="mt-6 space-y-4">
                            {SECTIONS.map((s) => (
                                <div key={s} className="lnl-card p-4">
                                    <div className="font-heading font-semibold text-lnl-charcoal">{s}</div>
                                    <div className="text-sm text-lnl-gray mt-1">Content will be provided by the {department?.name} once approved.</div>
                                </div>
                            ))}
                        </div>
                    </div>
                    <aside className="space-y-3 h-fit">
                        <div className="lnl-card p-4">
                            <div className="lnl-eyebrow">Career interests</div>
                            <div className="mt-2 flex flex-wrap gap-1.5">
                                {(program.career_tags || []).map((t) => <span key={t} className="text-xs bg-lnl-off-white border border-lnl-border rounded-full px-2 py-0.5">{t}</span>)}
                            </div>
                        </div>
                        <div className="lnl-card p-4">
                            <div className="lnl-eyebrow">Ready to apply?</div>
                            <div className="mt-2 space-y-2">
                                <Link to="/admissions/apply?type=college-freshman" className="lnl-btn-primary w-full justify-center">Apply now</Link>
                                <Link to="/admissions/apply?type=college-transferee" className="lnl-btn-secondary w-full justify-center">Apply as transferee</Link>
                                <Link to="/tuition-scholarships" className="lnl-link text-sm inline-flex items-center gap-1">Ask about tuition <ArrowRight className="h-3.5 w-3.5" /></Link>
                            </div>
                        </div>
                    </aside>
                </div>
            </section>
        </div>
    );
}
